/**
 * `@equerranz/portal-module-coa`: the npm half of `Equerra.Portal.Modules.Coa` (FLF-S1).
 *
 * Farmland's COA Automation module's frontend. Its default export is the manifest, which is what a
 * portal's generated registry imports by convention, following
 * `web/modules/skeleton/src/index.ts` in `EquerraNZ/eq-portal`.
 *
 * **The slug is the only thing the two halves share**, and it must equal the one the C# half declares.
 * `@equerranz/portal-testing`'s pair gate is what makes that true rather than intended.
 *
 * **What this package depends on is the frontend half of the module boundary.** The manifest contract, the
 * API client and the UI kit, and never `@equerranz/portal-shell`: that is the composition root, and a
 * module referencing it would be the frontend's version of a module referencing `Equerra.Portal.Hosting`,
 * which the .NET boundary gate fails a build over.
 */
import { portalLazy } from '@equerranz/portal-module-contract';
import { CertificateRegular, ClipboardTaskListLtrRegular, ClockRegular, DocumentCheckmarkRegular, HistoryRegular, TagRegular, } from '@equerranz/portal-ui/icons';
import { createElement } from 'react';
import { COA_SLUG } from './acceptanceReferences.js';
import { CoaOverdueLotsCountBadge } from './CoaOverdueLotsCountBadge.js';
import { CoaPendingReviewCountBadge } from './CoaPendingReviewCountBadge.js';
import { CoaReviewDetailBreadcrumb } from './CoaReviewDetailBreadcrumb.js';
/**
 * Lazily loaded through `portalLazy` (the framework's `React.lazy` for route pages, so the shell's Retry on a failed
 * page load really re-imports it), which is what makes a portal's bundle grow with what a caller opens rather than with what
 * the portal carries. The shell puts the loading state and the boundary around it, so a module writes
 * neither.
 *
 * **Nothing in this file may also import a page eagerly**: a static import beside one of these dynamic ones
 * puts the page in the entry chunk and `portalLazy` does nothing, the exact `INEFFECTIVE_DYNAMIC_IMPORT` trap
 * the framework's own reference module hit first.
 */
const AcceptanceReferencePageLazy = portalLazy(() => import('./AcceptanceReferencePage.js'));
const CoaPendingReviewLaunchPageLazy = portalLazy(() => import('./CoaPendingReviewLaunchPage.js'));
const CoaPendingReviewsPageLazy = portalLazy(() => import('./CoaPendingReviewsPage.js'));
const CoaReviewDetailPageLazy = portalLazy(() => import('./CoaReviewDetailPage.js'));
const CoaReviewHistoryPageLazy = portalLazy(() => import('./CoaReviewHistoryPage.js'));
const CoaReviewPageLazy = portalLazy(() => import('./CoaReviewPage.js'));
const KnownItemCodesPageLazy = portalLazy(() => import('./KnownItemCodesPage.js'));
const LotsOnHoldReleasePageLazy = portalLazy(() => import('./LotsOnHoldReleasePage.js'));
/**
 * This module's manifest.
 *
 * Every path is relative to the module's own root, and the empty path is the module's index: a portal's
 * shell mounts this module at `/coa` from the slug, so nothing here writes that prefix and nothing here
 * can write another module's or the framework's.
 *
 * **One navigation entry per page, in two groups (EPF-S149).** The shell draws one rail down the left on every page, with
 * one row per app: this module is the app "COA", and its navigation entries are the pages listed under it when it is
 * the current app, "COA Analysis" (Samples, Pending, COA Review, History: the day-to-day work) then "Setup" (Items,
 * Acceptance Criteria, visited far less often). This module used to declare one entry and draw those pages as a rail
 * of its own (`CoaModuleLayout`), because an entry was also a launcher tile; the launcher and the Apps page now show
 * one tile per module, so the pages belong in `navigation` and a second sidebar would only duplicate the shell's.
 * The first entry is where the app's tile, its rail row and its crumb open, so it is Samples, the day-to-day page and
 * never the Setup page at the root.
 *
 * **`requiredPermission` is this manifest's own gate on who sees a rail entry at all (FLF-S2), not a
 * `RoleGuard` wrapper.** `docs/module-ui-guidelines.md`'s "Gate what a person can see" section (written from
 * FLF-S1) shows `RoleGuard` wrapping a `PortalNavigationEntry` element, which predates
 * `@equerranz/portal-shell`'s own `composePortalNavigation`/`filterPortalNavigation` (EPF-S49, Correction 37
 * A6): a manifest's `navigation` array is data the shell filters with a plain `.filter()`, never a rendered
 * subtree a module could wrap in a guard component. `PortalModuleRoute.requiredPermission` is the real,
 * current mechanism, read here by the shell to decide whether each entry below appears; the route itself
 * still always mounts regardless (S49-D4), exactly as the API's own permission check still gates the request
 * either way.
 */
const manifest = {
    slug: COA_SLUG,
    // The slug's own naive capitalisation ("Coa") misnames the acronym this module actually is
    // (found live: the settings nav entry read "Coa settings"). PortalModuleManifest.displayName exists for
    // exactly this (EPF, portal-shell's adminNavigation.tsx).
    displayName: 'COA',
    // Every entry carries an icon (the icons-only rail tells entries apart by it). The first is the app's own icon: a lab
    // certificate is exactly what this module's pass/fail reference and COA review flow are about.
    //
    // A "Pending Reviews" live count stays out of the Apps grid on purpose (found live: it read as a second app).
    // `overviewSection` below is that glance instead.
    navigation: [
        { label: 'Samples', path: 'lots-on-hold-release', icon: createElement(CertificateRegular), group: 'COA Analysis' },
        { label: 'Pending', path: 'reviews/pending', icon: createElement(ClockRegular), group: 'COA Analysis' },
        { label: 'COA Review', path: 'reviews', icon: createElement(DocumentCheckmarkRegular), group: 'COA Analysis' },
        { label: 'History', path: 'reviews/history', icon: createElement(HistoryRegular), group: 'COA Analysis' },
        { label: 'Items', path: 'known-item-codes', icon: createElement(TagRegular), group: 'Setup' },
        { label: 'Acceptance Criteria', path: '', icon: createElement(ClipboardTaskListLtrRegular), group: 'Setup' },
    ],
    // Every route declares its own `breadcrumb` (EPF-S92), including the two record routes that no navigation entry
    // leads to. The labels are the page titles, so the trail (Home / COA / the list a record sits under) reads the same
    // as the headings. Since EPF-S149 the trail lists ancestors only, so a record page's own crumb is not drawn and a
    // component crumb is read only when its route is another route's `breadcrumbParent`; it is kept so a record page
    // still names itself wherever a crumb for it is wanted.
    routes: [
        { path: '', component: AcceptanceReferencePageLazy, breadcrumb: 'Acceptance Criteria' },
        {
            path: 'reviews',
            component: CoaReviewPageLazy,
            requiredPermission: 'coa.reference.read',
            breadcrumb: 'COA Review',
        },
        {
            path: 'reviews/pending',
            component: CoaPendingReviewsPageLazy,
            requiredPermission: 'coa.reference.read',
            breadcrumb: 'Pending',
        },
        {
            path: 'reviews/pending/:id',
            component: CoaPendingReviewLaunchPageLazy,
            requiredPermission: 'coa.reference.read',
            breadcrumb: 'Pending review',
            breadcrumbParent: 'reviews/pending',
        },
        {
            path: 'reviews/history',
            component: CoaReviewHistoryPageLazy,
            requiredPermission: 'coa.reference.read',
            breadcrumb: 'History',
        },
        {
            path: 'reviews/history/:id',
            component: CoaReviewDetailPageLazy,
            requiredPermission: 'coa.reference.read',
            breadcrumb: CoaReviewDetailBreadcrumb,
            // Home / COA Analysis / History / Review 123: the record sits under the list it was opened from.
            breadcrumbParent: 'reviews/history',
        },
        {
            path: 'known-item-codes',
            component: KnownItemCodesPageLazy,
            requiredPermission: 'coa.reference.read',
            breadcrumb: 'Items',
        },
        {
            path: 'lots-on-hold-release',
            component: LotsOnHoldReleasePageLazy,
            requiredPermission: 'coa.reference.read',
            breadcrumb: 'Samples',
        },
    ],
    // A glance at what is outstanding on login, in the portal's own Overview section beneath the Apps grid. `title`
    // names what the section is about, independent of the app's own name, on `PortalModuleOverviewSection`'s precedent. `path` names the same
    // route the module's own "reviews/pending" page mounts at, so the framework resolves this fact's required
    // permission from that route's own declaration above rather than a second copy here.
    // `CoaPendingReviewCountBadge` is the same live-count component this section's only fact carried before
    // (EPF-S89-D1's own dashboardWidget); only where it renders, and what else could join it, has changed.
    // `CoaOverdueLotsCountBadge` joined it the same way: `LotsOnHoldReleasePage`'s own Overdue status,
    // counted rather than only shown row by row, on a QA reviewer's own "let me see this on login too"
    // ask. More facts belong here as COA grows more things worth a glance, each its own object in this array.
    overviewSection: {
        title: 'COA Analysis',
        facts: [
            // `interactive`: the shell draws a plain card, not a link, so the `PortalFigure` inside opens its drawer (framework 0.10.0).
            { path: 'reviews/pending', content: createElement(CoaPendingReviewCountBadge), interactive: true },
            { path: 'lots-on-hold-release', content: createElement(CoaOverdueLotsCountBadge), interactive: true },
        ],
    },
    // FLF-S8: the C# half now declares SettingsSchema (Provider, API key, Model), gated on
    // coa.settings.manage. This flag is what turns on the framework's own generic, schema-driven settings
    // page and admin nav entry for this module (EPF-S84's adminNavigation.tsx and PortalModuleSettingsPage.tsx,
    // both read live from GET /v1/modules/coa/settings): no COA-specific frontend code beyond this flag,
    // confirmed by reading PortalModuleSettingsPage's own source before treating that as settled.
    hasSettings: true,
};
export default manifest;
export { acceptanceReferencesQueryOptions, COA_SLUG, coaQueryKeys, createAcceptanceReference, listAcceptanceReferences, } from './acceptanceReferences.js';
export { coaReviewQueryKeys, createKnownItemCode, knownItemCodesQueryOptions, listKnownItemCodes, reviewCoaSubmission, uploadCoaReviewFile, } from './coaReviews.js';
export { coaLotOnHoldReleaseQueryKeys, listLotsOnHoldRelease, } from './lotsOnHoldRelease.js';
export { coaPendingReviewQueryKeys, getPendingCoaReview, listPendingCoaReviews, pendingCoaReviewQueryOptions, pendingCoaReviewsQueryOptions, } from './coaPendingReviews.js';
//# sourceMappingURL=index.js.map